/**
 * ThemeContext — theme selection driving both CSS variables AND which
 * layout components render (editorial/studio/utility each have a
 * different shell + dashboard structure).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type ThemeId = 'editorial' | 'studio' | 'utility';

export const THEMES: {
  id: ThemeId;
  label: string;
  description: string;
  swatch: string[];
}[] = [
  {
    id: 'editorial',
    label: 'Editorial',
    description:
      'Magazine-style masthead, hairline charcoal dividers between rows, big display type, Terra Cotta accents.',
    swatch: ['#FFFFFF', '#2B2A2F', '#B16C4C', '#EAE9E5'],
  },
  {
    id: 'studio',
    label: 'Studio',
    description:
      'Warm Cloud-tinted page, borderless cards with soft shadows, big display numbers, Pine + Sage accents.',
    swatch: ['#F3F1EB', '#FFFFFF', '#375542', '#C3C6A7'],
  },
  {
    id: 'utility',
    label: 'Utility',
    description:
      'Dark sidebar nav, data-table row layout, compact density, Jade interactive accents. Admin-tool feel.',
    swatch: ['#FBFAF8', '#2B2A2F', '#769D91', '#EAE9E5'],
  },
];

const STORAGE_KEY = 'ojb.theme';
// Studio is the only active theme the app renders today. Editorial +
// Utility remain in the code for future re-use but are no longer
// selectable. Any legacy stored value is migrated to 'studio' on
// next page load so users whose browsers still have
// localStorage['ojb.theme']='editorial' (from the old theme picker
// days) automatically upgrade.
const DEFAULT_THEME: ThemeId = 'studio';

function readStoredTheme(): ThemeId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v !== 'studio') {
      // Overwrite any legacy value so this migration runs once per
      // browser, not every page load.
      localStorage.setItem(STORAGE_KEY, DEFAULT_THEME);
    }
  } catch {
    // ignore — localStorage might be blocked (private browsing etc.)
  }
  return DEFAULT_THEME;
}

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (t: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(readStoredTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeState }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
